import React from 'react';

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Custom color override (defaults to brand color #13AFF0) */
  color?: string;
  /** Optional text to display next to spinner */
  text?: string;
  /** Layout variant */
  variant?: 'inline' | 'centered' | 'fullscreen';
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

const containerClasses = {
  inline: 'flex items-center gap-3',
  centered: 'flex items-center justify-center gap-3',
  fullscreen: 'min-h-screen flex items-center justify-center gap-3',
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color,
  text,
  variant = 'inline',
  className = '',
}) => {
  const spinnerClass = `animate-spin rounded-full border-2 border-transparent border-t-current ${sizeClasses[size]}`;
  const containerClass = `${containerClasses[variant]} ${className}`;

  // Use brand color by default via class, or custom color via style if provided
  const spinnerStyle = color ? { color } : undefined;
  // If no custom color is provided, add text-brand class to use the configured brand color
  const spinnerColorClass = !color ? 'text-brand' : '';

  return (
    <div className={containerClass} role='status' aria-live='polite'>
      <div
        className={`${spinnerClass} ${spinnerColorClass}`}
        style={spinnerStyle}
        aria-hidden='true'
      />
      {text && <span className='text-sm text-gray-600 font-circular'>{text}</span>}
    </div>
  );
};

// Common preset variants for convenience
export const LoadingSpinnerCentered: React.FC<Omit<LoadingSpinnerProps, 'variant'>> = (props) => (
  <LoadingSpinner {...props} variant='centered' />
);

export const LoadingSpinnerFullscreen: React.FC<Omit<LoadingSpinnerProps, 'variant'>> = (props) => (
  <LoadingSpinner {...props} variant='fullscreen' />
);

export const LoadingSpinnerInline: React.FC<Omit<LoadingSpinnerProps, 'variant'>> = (props) => (
  <LoadingSpinner {...props} variant='inline' />
);
